import { Search, X } from "lucide-react"
import { forwardRef } from "react"

interface Props {
  query: string
  setQuery: (value: string) => void
  onFocus: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  controlsId: string
  isOpen: boolean
  // "dropdown" sits in the desktop header; "sheet" is the top bar of the
  // full-screen mobile search.
  variant?: "dropdown" | "sheet"
}

const isMac = () => {
  return (
    typeof navigator !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0
  )
}

const PageSearchForm = forwardRef<HTMLInputElement, Props>(
  (
    {
      query,
      setQuery,
      onFocus,
      onKeyDown,
      controlsId,
      isOpen,
      variant = "dropdown"
    },
    ref
  ) => {
    const shortcutKey = isMac() ? "⌘" : "Ctrl"
    const isSheet = variant === "sheet"

    const placeholder = isSheet
      ? "Name, email, NMLS or ID"
      : "Search loan officers, brands, leads, subscriptions…"

    return (
      <div
        className={`relative w-full group ${
          isSheet ? "" : "sm:max-w-[calc(42rem*0.75)]"
        }`}
      >
        <div
          className={`absolute inset-y-0 flex items-center pointer-events-none ${
            isSheet ? "left-3" : "left-4"
          }`}
        >
          <Search
            className="text-slate-500 group-focus-within:text-primary-400 transition-colors"
            size={isSheet ? 18 : 20}
            aria-hidden="true"
          />
        </div>
        <form autoComplete="off" onSubmit={e => e.preventDefault()}>
          <label htmlFor="search-field" className="sr-only">
            Search
          </label>
          <input
            ref={ref}
            id="search-field"
            className={`bg-zinc-100 block w-full h-full border-transparent rounded-xl text-base-dark placeholder-slate-500 focus:ring-2 focus:ring-primary-400/20 focus:bg-slate-100 focus:placeholder-slate-400 focus:outline-none focus:border-primary-400 [&::-webkit-search-cancel-button]:appearance-none ${
              isSheet
                ? // 16px text keeps iOS from zooming the page on focus
                  "pl-10 pr-10 py-2.5 text-base"
                : "pl-12 pr-16 py-2.5 text-sm"
            }`}
            placeholder={placeholder}
            type="search"
            name="search"
            autoComplete="off"
            autoFocus={isSheet}
            enterKeyHint="search"
            role="combobox"
            aria-controls={controlsId}
            aria-expanded={isOpen}
            aria-autocomplete="list"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
          />
        </form>
        {isSheet ? (
          query.length > 0 && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setQuery("")}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 focus:outline-none focus-visible:text-primary-400"
            >
              <X size={18} aria-hidden="true" />
            </button>
          )
        ) : (
          <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
            <div className="inline-flex items-center px-2 py-0.5 text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-md shadow-sm">
              <span>{shortcutKey}</span>
              <span className="ml-1.5">K</span>
            </div>
          </div>
        )}
      </div>
    )
  }
)

PageSearchForm.displayName = "PageSearchForm"

export default PageSearchForm
